import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  getQuotationById,
  sendQuotation,
  duplicateQuotation,
  convertQuotationToPurchase,
  deleteQuotation,
  getBranchTaxes,
} from '../api';
import QuoteItemsTable from '../components/QuoteItemsTable';
import { hasPermission } from '../utils/permissions';
import { getQuotationStatusLabel } from '../utils/quotationStatus';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const QuotationDetailPage = () => {
  const { branchId, id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [taxes, setTaxes] = useState([]);
  const { user } = useCurrentUser();
  const canUpdateQuotation = hasPermission(user, 'quotation.update');
  const canCreateQuotation = hasPermission(user, 'quotation.create');
  const canDeleteQuotation = hasPermission(user, 'quotation.delete');
  const canReadTaxes = hasPermission(user, 'tax.read') || hasPermission(user, 'tax.exclude');
  const { confirm } = useConfirmDialog();

  const fetchQuotation = async () => {
    try {
      const data = await getQuotationById(branchId, id);
      setQuotation(data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar la cotización');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotation();
  }, [branchId, id]);

  useEffect(() => {
    if (!branchId || !canReadTaxes) {
      setTaxes([]);
      return;
    }
    let isMounted = true;
    const loadTaxes = async () => {
      try {
        const data = await getBranchTaxes(branchId);
        if (!isMounted) return;
        setTaxes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        console.error(err);
        setTaxes([]);
      }
    };
    loadTaxes();
    return () => {
      isMounted = false;
    };
  }, [branchId, canReadTaxes]);

  const handleSend = async () => {
    if (!canUpdateQuotation) return;
    if (quotation?.status === 'sent' || quotation?.status === 'accepted') {
      notify('La cotización ya fue enviada o aceptada.');
      return;
    }
    if (!quotation?.Client?.email) {
      notify('El cliente no tiene correo registrado, no se puede enviar.');
      return;
    }
    try {
      await sendQuotation(branchId, id, {});
      notify('Correo enviado exitosamente');
      fetchQuotation();
    } catch (err) {
      console.error(err);
      notify('No se pudo enviar el correo, verifique el correo del cliente e intente nuevamente');
    }
  };

  const handleDuplicate = async () => {
    if (!canCreateQuotation) return;
    try {
      const resp = await duplicateQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations/${resp.quotation.id}`);
    } catch (err) {
      console.error(err);
      notify('No se pudo duplicar la cotización');
    }
  };

  const handleConvert = async () => {
    if (!canUpdateQuotation) return;
    const approved = await confirm({
      title: 'Convertir cotización',
      message: 'Esto creará una venta sin pagar. ¿Continuar?',
      confirmLabel: 'Continuar',
      cancelLabel: 'Cancelar',
      tone: 'warning',
    });
    if (!approved) return;
    try {
      const response = await convertQuotationToPurchase(branchId, id);
      const purchaseId = response?.purchase?.id;
      if (purchaseId) {
        notify('Cotización convertida. Redirigiendo a registrar pago.');
        navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt`);
        return;
      }
      notify('Cotización convertida, pero no se encontró la venta creada.');
      fetchQuotation();
    } catch (err) {
      console.error(err);
      notify('No se pudo convertir la cotización');
    }
  };

  const handleDelete = async () => {
    if (!canDeleteQuotation) return;
    const approved = await confirm({
      title: 'Eliminar cotización',
      message: '¿Eliminar esta cotización?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    try {
      await deleteQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations`);
    } catch (err) {
      console.error(err);
      notify('No se pudo eliminar la cotización');
    }
  };

  if (loading) {
    return (
      <p className="p-4">
        <InlineLoading label="Cargando cotización" />
      </p>
    );
  }
  if (error) return <p className="p-4 text-red-600">{error}</p>;
  if (!quotation) return <p className="p-4">No se encontró la cotización.</p>;

  const excludedTaxIds = Array.isArray(quotation.excludedTaxIds) ? quotation.excludedTaxIds : [];
  const taxNameById = new Map(
    (taxes || []).map((tax) => [Number(tax.id), tax.name || `Impuesto ${tax.id}`])
  );
  const excludedTaxLabels = excludedTaxIds
    .map((id) => taxNameById.get(Number(id)) || `#${id}`)
    .filter(Boolean);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto bg-white shadow rounded p-6">
        <div className="flex justify-between items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Cotización #{quotation.id}</h1>
            <p className="text-sm text-gray-600">
              Estado: <span className="font-semibold">{getQuotationStatusLabel(quotation.status)}</span>
            </p>
            <p className="text-xs text-gray-500">
              Actualizada: {new Date(quotation.updatedAt).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdateQuotation && quotation.status !== 'sent' && quotation.status !== 'accepted' && (
              <button
                onClick={() => navigate(`/branches/${branchId}/quotations/${quotation.id}/edit`)}
                className="px-3 py-2 bg-blue-600 text-white rounded text-sm"
              >
                Editar
              </button>
            )}
            {canCreateQuotation && (
              <button onClick={handleDuplicate} className="px-3 py-2 bg-yellow-500 text-white rounded text-sm">
                Duplicar
              </button>
            )}
            {canUpdateQuotation && (
              <button
                onClick={handleSend}
                disabled={quotation.status === 'sent' || quotation.status === 'accepted' || !quotation.Client?.email}
                className="px-3 py-2 bg-green-600 text-white rounded text-sm disabled:opacity-60"
                title={!quotation.Client?.email ? 'El cliente no tiene correo' : undefined}
              >
                Enviar
              </button>
            )}
            {canUpdateQuotation && (
              <button onClick={handleConvert} className="px-3 py-2 bg-teal-600 text-white rounded text-sm">
                Convertir
              </button>
            )}
            {canDeleteQuotation && (
              <button onClick={handleDelete} className="px-3 py-2 bg-red-600 text-white rounded text-sm">
                Eliminar
              </button>
            )}
            <button
              onClick={() => window.print()}
              className="px-3 py-2 bg-gray-200 rounded text-sm"
            >
              Imprimir
            </button>
            <button
              onClick={() => navigate(`/branches/${branchId}/quotations`)}
              className="px-3 py-2 bg-gray-200 rounded text-sm"
            >
              Volver
            </button>
          </div>
        </div>

        {quotation.Client && (
          <div className="mt-4 border rounded p-4 bg-gray-50">
            <h2 className="font-semibold mb-1">Cliente</h2>
            <p className="text-sm">{quotation.Client.name}</p>
            {quotation.Client.rut && <p className="text-sm text-gray-600">RUT: {quotation.Client.rut}</p>}
            {quotation.Client.email && <p className="text-sm text-gray-600">Email: {quotation.Client.email}</p>}
            {!quotation.Client.email && (
              <p className="text-xs text-red-600">Sin correo: no se puede enviar por email.</p>
            )}
          </div>
        )}

        <h2 className="text-lg font-semibold mt-6 mb-2">Productos</h2>
        <QuoteItemsTable
          items={quotation.items}
          itemsSubtotal={quotation.itemsSubtotal}
          orderDiscountAmount={quotation.orderDiscountAmount}
          totalAmount={quotation.totalAmount}
          netTotal={quotation.netTotal}
          taxBreakdown={quotation.taxBreakdown}
          excludedTaxLabels={excludedTaxLabels}
        />
      </div>
    </div>
  );
};

export default QuotationDetailPage;
